import { createSelector, createSlice, Dictionary, PayloadAction } from '@reduxjs/toolkit';
import { toDictionary } from '@ff-os/shared-utils/dictionaries';
import { PdpState } from '../store';

type Owner = {
    readonly id: string;
    readonly name: string;
    readonly logoAssetId?: string;
};

type Resource = {
    type: string;
    content: {
        id: string;
        [key: string]: any;
    };
    owner: Owner | null;
    title?: { [languageCode: string]: string };
    description?: { [languageCode: string]: string };
    previewImagesUrls?: string[];
};

export type ListResourcesResult = {
    resourceType: string;
    entries: Resource[];
};

export const { actions, reducer } = createSlice({
    name: 'resources',
    initialState: {} as Dictionary<any>,
    reducers: {

        replace: (state, action: PayloadAction<ListResourcesResult & { language: string }>) => {

            const resourceType = action.payload.resourceType;
            const language = action.payload.language || 'en-US';
            const resources = action.payload.entries
                .filter(e => e.type === resourceType)
                .map(e => addOwnerToResourceIfExists(e, language));

            state[resourceType] = toDictionary(resources, r => r.id);
        }
    }
});

const addLanguageToPreviewUrl = (url: string, language: string): string => {
    const queryIndex = url.indexOf('?');
    if (queryIndex === -1) {
        return `${url}/${language}`;
    }
    return `${url.substring(0, queryIndex)}/${language}${url.substring(queryIndex)}`;
};

const addOwnerToResourceIfExists = (resource: Resource, language: string) => {

    const title = resource.title?.[language] || resource.title?.['en-US'];
    const description = resource.description?.[language] || resource.description?.['en-US'];
    const previewImagesUrls = resource.previewImagesUrls?.map(url => addLanguageToPreviewUrl(url, language));

    if (resource.owner) {
        return {
            ...resource.content,
            title,
            description,
            previewImagesUrls,
            commission: {
                id: resource.owner.id,
                logoAssetId: resource.owner.logoAssetId,
                name: resource.owner.name
            }
        };
    } else {
        return {
            ...resource.content,
            title,
            description,
            previewImagesUrls
        };
    }
};

export const selectResources = <TResource>(type: string) => createSelector(
    (state: PdpState) => state.resources,
    (resources) => resources[type] as Dictionary<TResource>);